import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';
import SafetyEvolucaoCharts from './SafetyEvolucaoCharts';
import {
  NearMissDistributionCharts,
  UnitCompareBarChart,
} from './SafetyDistributionCharts';

function canSeeCorporate(auth) {
  if (!auth) return false;
  if (auth.isAdmin || auth.isCargoCorporativo) return true;
  if (auth.user?.canAccessAllUnidades) return true;
  const role = String(auth.rbacRole || auth.user?.role || '').toUpperCase();
  return ['SUPER_ADMIN', 'CORPORATE_MANAGER', 'ADMIN', 'GLOBAL_VIEWER'].includes(role);
}

export default function SafetyHuddleOverviewCorporativoPage() {
  const auth = useAuth();
  const { unidadeSelecionada } = auth;
  const allowed = canSeeCorporate(auth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!allowed || !unidadeSelecionada?.codigo) return;
    setLoading(true);
    setError('');
    try {
      const d = await shJson(
        '/api/safety-huddle/dashboard/corporativo?months=12',
        unidadeSelecionada
      );
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [allowed, unidadeSelecionada]);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener('refreshPage', onRefresh);
    return () => window.removeEventListener('refreshPage', onRefresh);
  }, [load]);

  if (!allowed) {
    return <Navigate to="/safety-huddle/dashboard" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const t = data?.totals || {};

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="primary.main">
        Visão corporativa · Rede
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Comparativo de Safety Huddle entre todas as unidades ativas (Matriz)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip color="primary" label={`${t.unidades || 0} unidades`} />
        <Chip label={`${t.setores || 0} setores`} />
        <Chip label={`${t.formularios || 0} formulários`} />
        <Chip label={`${t.envios || 0} envios`} />
        <Chip color="warning" label={`${t.nearMisses || 0} quase-falhas`} />
      </Box>

      <SafetyEvolucaoCharts data={data?.evolucao || []} title="Rede · últimos 12 meses" />

      <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
        Por unidade
      </Typography>
      <UnitCompareBarChart byUnidade={data?.byUnidade || []} />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {(data?.byUnidade || []).map((u) => (
          <Grid item xs={12} sm={6} md={4} key={u.codigo}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(25,118,210,0.12)',
              }}
            >
              <Typography fontWeight={700}>{u.nome}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {u.codigo}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {u.setores} setor(es) · {u.formularios} form. · {u.envios} envios · {u.quaseFalhas}{' '}
                quase-falhas
              </Typography>
              <Box sx={{ mt: 1.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Taxa de resposta
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    {u.taxaResposta}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, u.taxaResposta || 0)}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {u.templatesComEnvio || 0}/{u.formularios || 0} formulários com envio
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
        {!data?.byUnidade?.length && (
          <Grid item xs={12}>
            <Alert severity="info">Nenhuma unidade ativa encontrada.</Alert>
          </Grid>
        )}
      </Grid>

      <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
        Quase-falhas (rede)
      </Typography>
      <Box sx={{ mb: 3 }}>
        <NearMissDistributionCharts
          bySeverity={data?.nearMissBySeverity}
          byStatus={data?.nearMissByStatus}
          title="Rede"
        />
      </Box>
    </Box>
  );
}
